import React from 'react';
import { HelpCircle, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface WizardTooltipProps {
  content: string;
  className?: string;
  iconSize?: 'sm' | 'md';
  variant?: 'help' | 'info';
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export function WizardTooltip({
  content,
  className,
  iconSize = 'sm',
  variant = 'help',
  side = 'top',
}: WizardTooltipProps) {
  const Icon = variant === 'help' ? HelpCircle : Info;
  const iconSizeClass = iconSize === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'h-auto w-auto p-0 hover:bg-transparent',
              className
            )}
          >
            <Icon className={cn(
              iconSizeClass,
              'text-gray-400 hover:text-gray-600 transition-colors'
            )} />
            <span className="sr-only">Help</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent 
          side={side}
          className="max-w-xs text-sm"
        >
          <p>{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Standalone help icon that can be placed inline
export function InlineHelp({ content, className }: { content: string; className?: string }) {
  return (
    <WizardTooltip
      content={content}
      className={cn('inline-flex ml-1 align-middle', className)}
      iconSize="sm"
      variant="help"
    />
  );
}

// Section help that appears next to titles
export function SectionHelp({ content }: { content: string }) {
  return (
    <WizardTooltip
      content={content}
      className="ml-2"
      iconSize="md"
      variant="info"
    />
  );
}
